import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {Offer} from "types/Offer";
import {useNotifications} from "context/NotificationContext";
import Button from "components/Button";
import {PaperAirplaneIcon, PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import {ChartBarIcon, GiftIcon, UsersIcon} from "@heroicons/react/outline";
import DeleteOffer from "pages/Offer/Delete";
import StatList from "components/StatList";
import StatBox from "components/StatBox";
import {duration, formattedDateTime} from "services/date";
import Link from "components/Link";
import Chip from "components/Chip";
import OfferDistributionStatus from "../../../types/OfferDistributionStatus";
import DistributeOffer from "../Distribute";
import DotBadge from "../../../components/DotBadge";

// Helpers ----

// Map message status to badge color
const statusColors: Record<OfferDistributionStatus, number> = {
  [OfferDistributionStatus.Pending]: DotBadge.colors.YELLOW,
  [OfferDistributionStatus.Ongoing]: DotBadge.colors.BLUE,
  [OfferDistributionStatus.Complete]: DotBadge.colors.GREEN,
  [OfferDistributionStatus.Failed]: DotBadge.colors.RED,
}

function offerCanBeDistributed(offer?: Offer | null): boolean {
  if (!offer) return false;

  return offer.distribution_status == null ||
    offer.distribution_status === OfferDistributionStatus.Failed ||
    offer.distribution_status === OfferDistributionStatus.Complete
}

// Component ----

function ViewOffer() {
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const {id} = useParams();

  const {data: offer, isLoading} = useQuery(
    ['offer', {id}],
    () => ngage.offers.get(parseInt(id as string))
  )

  const [distributingOffer, setDistributingOffer] = useState<Offer | null>(null);
  const [deletingOffer, setDeletingOffer] = useState<Offer | null>(null);

  // Prepare action handlers
  function handleDeleteOffer() {
    if (offer) setDeletingOffer(offer)
  }

  function handleEditOffer() {
    if (id) navigate(`/offers/${id}/edit`)
  }

  function handleDistributeOffer() {
    if (offer) setDistributingOffer(offer)
  }

  // Prepare page actions
  const actions = (<>
    <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteOffer}
            disabled={!offer || offer.was_issued}>Delete</Button>
    <Button color={Button.colors.WHITE} icon={PencilIcon} onClick={handleEditOffer}
            disabled={!offer || offer.was_issued}>Edit</Button>
    <Button color={Button.colors.PRIMARY} icon={PaperAirplaneIcon} onClick={handleDistributeOffer}
            disabled={!offerCanBeDistributed(offer)}>Distribute...</Button>
  </>)

  return (
    <Page
      title={offer?.name || "View Offer"}
      breadcrumbs={{"Offers": "/offers", [offer?.name || "View Offer"]: `/offers/${id}`}}
      actions={actions}
      isLoading={isLoading}
    >
      {offer ?
        <>
          {/* Offer Stats */}
          <section>
            <StatList>
              <StatBox name="Holders" stat={offer.holder_count} icon={UsersIcon}/>
              <StatBox name="Vouchers Issued" stat={offer.voucher_count} icon={GiftIcon}/>
              <StatBox name="Redemption Rate" stat={`${offer.redemption_rate}%`} icon={ChartBarIcon}/>
            </StatList>
          </section>

          {/* Offer Information */}
          <section className="mt-4 lg:flex lg:flex-row lg:flex-row-reverse lg:justify-end">
            {offer.image &&
              <div
                className="h-36 sm:mb-2 sm:shadow sm:rounded-md sm:overflow-hidden lg:basis-1/3 lg:h-auto lg:mb-0 lg:ml-4">
                <img
                  src={offer.image.preview_url ?? offer.image.original_url}
                  alt={offer.name}
                  className="object-cover h-full w-full"
                />
              </div>
            }

            <div className="lg:basis-2/3 lg:flex-shrink-0 lg:flex-grow">
              <Card
                heading="Offer Information"
                subheading={`${offer.is_valid ? 'Valid' : 'Expired'} offer`}
              >
                <DescriptionList>

                  <DescriptionListItem span={2}>
                    <DescriptionTerm>Incentive</DescriptionTerm>
                    <DescriptionDetails>
                      {offer.incentive}
                    </DescriptionDetails>
                  </DescriptionListItem>

                  {offer.description &&
                    <DescriptionListItem span={2}>
                      <DescriptionTerm>Description</DescriptionTerm>
                      <DescriptionDetails>
                        <p className="whitespace-pre-line">{offer.description}</p>
                      </DescriptionDetails>
                    </DescriptionListItem>
                  }

                </DescriptionList>
              </Card>
            </div>
          </section>

          <section className="mt-4">
            <Card heading="Usage">
              <DescriptionList>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Valid At</DescriptionTerm>
                  <DescriptionDetails>
                    {formattedDateTime(offer.valid_at)}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Expires At</DescriptionTerm>
                  <DescriptionDetails>
                    {offer.expires ?
                      (offer.expires_at ? formattedDateTime(offer.expires_at) : `After ${duration(offer.valid_for)?.toHuman()}`) :
                      'Never'
                    }
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Last Issued At</DescriptionTerm>
                  <DescriptionDetails>
                    {offer.last_issued_at ? formattedDateTime(offer.last_issued_at) : 'Never'}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Latest Distribution Status</DescriptionTerm>
                  <DescriptionDetails>
                    {offer.distribution_status &&
                      <DotBadge color={statusColors[offer.distribution_status]} text={offer.distribution_status}/>
                    }

                    {!offer.distribution_status &&
                      <DotBadge color={DotBadge.colors.GRAY} text={'N/A'}/>
                    }
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Max Uses</DescriptionTerm>
                  <DescriptionDetails>
                    {offer.uses ?? 'Unlimited'}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Code</DescriptionTerm>
                  <DescriptionDetails>
                    {offer.code ?? 'Dynamically Generated'}
                  </DescriptionDetails>
                </DescriptionListItem>

                {offer.terms_url ?
                  <DescriptionListItem span={1}>
                    <DescriptionTerm>Terms & Conditions</DescriptionTerm>
                    <DescriptionDetails>
                      <Link to={offer.terms_url}>View</Link>
                    </DescriptionDetails>
                  </DescriptionListItem>
                  : null}
              </DescriptionList>
            </Card>
          </section>

          {!!offer.organisations?.length &&
            <section className="mt-4">
              <Card heading="Organisations" subheading="The organisations for which this offer applies">
                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {offer.organisations.map((organisation, index) => (
                    <li key={index}>
                      <Chip
                        name={organisation.name}
                        href={`/organisations/${organisation.id}`}
                        backgroundSrc={organisation.logo?.preview_url}
                      />
                    </li>
                  ))}
                </ul>
              </Card>
            </section>
          }

          {/* Terms and Conditions (text only) */}
          {offer.terms_text ?
            <section className="mt-4">
              <Card heading="Terms and Conditions">
                <p className="whitespace-pre-line">{offer.terms_text}</p>
              </Card>
            </section>
            : null}
        </>
        : null}

      {/* Distribute confirmation modal */}
      <DistributeOffer
        show={!!distributingOffer}
        offer={distributingOffer}
        onCancel={() => setDistributingOffer(null)}
        onDistribute={() => setDistributingOffer(null)}
      />

      {/* Delete confirmation modal */}
      <DeleteOffer
        show={offer === deletingOffer}
        offer={deletingOffer}
        onCancel={() => setDeletingOffer(null)}
        onDelete={offer => {
          navigate('/offers')
          notify("success", "Offer Deleted", `${offer.name} has been permanently removed`)
        }}
        onDeleteFail={offer => {
          setDeletingOffer(null)
          notify("error", "Offer Not Deleted", `${offer.name} could not be removed`)
        }}
      />
    </Page>
  );
}

export default ViewOffer;